import { createRoute, Link } from "@tanstack/solid-router";
import { For, type JSX } from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  ArrowRight,
  Boxes,
  Cloud,
  FolderSync,
  HardDrive,
  MonitorSmartphone,
  QrCode,
  Server,
  ShieldCheck,
} from "@fleet/icons";

import { appRoute } from "./route";
import { cn } from "@bodega/shared";
import { useI18n } from "@/providers/i18n-provider";
import { buttonVariants } from "@/components/ui/button";

/**
 * Bodega — Home dashboard. Warm and organized panel presenting the three
 * pillars of the product (BYOS self-storage, synchronized folders, and the
 * private P2P cloud between devices) and provides direct entry to real QR
 * pairing. Reflects honest startup state (nothing connected yet) with
 * premium empty states, instead of fictional data. Uses design system
 * tokens (no hardcoded colors) → light/dark theme-aware.
 */

interface StatProps {
  icon: (p: { class?: string }) => JSX.Element;
  value: string;
  label: string;
}

function Stat(props: StatProps) {
  return (
    <div class="relative overflow-hidden rounded-xl border border-border/60 bg-card/60 p-4 backdrop-blur-sm">
      <div class="flex items-center gap-3">
        <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/12 text-accent">
          <props.icon class="h-5 w-5" />
        </span>
        <div class="min-w-0">
          <p class="text-2xl font-bold leading-none tracking-tight text-foreground">
            {props.value}
          </p>
          <p class="mt-1 truncate text-xs font-medium text-muted-foreground">{props.label}</p>
        </div>
      </div>
    </div>
  );
}

interface PillarProps {
  icon: (p: { class?: string }) => JSX.Element;
  title: string;
  description: string;
  children?: JSX.Element;
}

function Pillar(props: PillarProps) {
  return (
    <div class="group relative flex flex-col overflow-hidden rounded-2xl border border-accent/20 bg-card p-5 shadow-sm transition-[transform,box-shadow,border-color] duration-200 motion-safe:hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-md hover:shadow-accent/10">
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-[radial-gradient(circle,var(--glow-0),transparent_70%)] opacity-80"
      />
      <span class="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-accent/20 to-accent/5 text-accent ring-1 ring-accent/15">
        <props.icon class="h-5 w-5" />
      </span>
      <h3 class="text-base font-semibold tracking-tight text-foreground">{props.title}</h3>
      <p class="mt-1.5 text-sm leading-relaxed text-muted-foreground">{props.description}</p>
      <div class="mt-4">{props.children}</div>
    </div>
  );
}

function Chip(props: { children: JSX.Element }) {
  return (
    <span class="inline-flex items-center rounded-full border border-border/70 bg-surface-secondary px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
      {props.children}
    </span>
  );
}

function DashboardPage() {
  const { t } = useI18n();

  // oxlint-disable-next-line lingui/no-unlocalized-strings -- storage provider brand names, not UI copy
  const providers = ["S3", "Cloudflare R2", "Backblaze B2", "WebDAV", "Nextcloud"];

  return (
    <div data-testid="dashboard" class="relative flex-1 overflow-y-auto">
      {/* Warm atmospheric wash behind the hero */}
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-[radial-gradient(60%_100%_at_50%_-10%,var(--glow-0),transparent_70%)]"
      />

      <div class="mx-auto w-full max-w-5xl px-4 py-8 md:px-8 md:py-10">
        {/* Hero */}
        <header class="flex flex-col items-start gap-3">
          <span class="inline-flex items-center gap-1.5 rounded-full border border-accent/25 bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
            <ShieldCheck class="h-3.5 w-3.5" />
            {t(msg`Your files, your storage`)}
          </span>
          <h1 class="text-balance text-3xl font-bold tracking-tight text-foreground md:text-4xl">
            {t(msg`Your storehouse. Your private cloud.`)}
          </h1>
          <p class="max-w-2xl text-pretty text-sm leading-relaxed text-muted-foreground md:text-base">
            {t(
              msg`Bodega syncs your folders across every device using storage you already own — S3, R2, Backblaze, WebDAV or Nextcloud. Your files never touch our servers.`,
            )}
          </p>
        </header>

        {/* Stats — honest boot state */}
        <section class="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Stat icon={Server} value="0" label={t(msg`Connected remotes`)} />
          <Stat icon={FolderSync} value="0" label={t(msg`Synced folders`)} />
          <Stat icon={MonitorSmartphone} value="1" label={t(msg`Trusted devices`)} />
        </section>

        {/* Pillars */}
        <section class="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
          <Pillar
            icon={HardDrive}
            title={t(msg`Bring your own storage`)}
            description={t(
              msg`Connect the storage you already pay for. Bodega keeps only an encrypted index — the content stays yours.`,
            )}
          >
            <div class="flex flex-wrap gap-1.5">
              <For each={providers}>{(p) => <Chip>{p}</Chip>}</For>
            </div>
          </Pillar>

          <Pillar
            icon={FolderSync}
            title={t(msg`Synced folders`)}
            description={t(
              msg`Pick exactly which folders sync to which devices. Offline edits on two devices converge with no data loss — both versions are kept.`,
            )}
          >
            <div class="flex flex-wrap gap-1.5">
              <Chip>{t(msg`Selective`)}</Chip>
              <Chip>{t(msg`Versioned`)}</Chip>
              <Chip>{t(msg`Conflict-safe`)}</Chip>
            </div>
          </Pillar>

          <Pillar
            icon={Cloud}
            title={t(msg`Private P2P cloud`)}
            description={t(
              msg`Your devices sync directly to each other. The server only brokers trust and relays sealed bytes — never the content.`,
            )}
          >
            <Link
              to="/profile/transfer-device"
              class="inline-flex items-center gap-1 text-sm font-semibold text-accent hover:underline"
            >
              {t(msg`Pair a device`)}
              <ArrowRight class="h-4 w-4" />
            </Link>
          </Pillar>
        </section>

        {/* Private cloud panel */}
        <section class="mt-8">
          <div class="overflow-hidden rounded-2xl border border-accent/20 bg-card shadow-sm">
            <div class="flex flex-col gap-4 border-b border-border/50 bg-gradient-to-br from-accent/8 to-transparent p-5 sm:flex-row sm:items-center sm:justify-between">
              <div class="flex items-start gap-3">
                <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground shadow-sm">
                  <Cloud class="h-5 w-5" />
                </span>
                <div>
                  <h2 class="text-lg font-semibold tracking-tight text-foreground">
                    {t(msg`Your private cloud`)}
                  </h2>
                  <p class="mt-0.5 text-sm text-muted-foreground">
                    {t(msg`Trusted devices that sync directly, no third party in between.`)}
                  </p>
                </div>
              </div>
              <div class="flex shrink-0 gap-2">
                <Link
                  to="/profile/receive-data"
                  class={cn(buttonVariants({ variant: "outline" }), "gap-2")}
                >
                  {t(msg`Receive`)}
                </Link>
                <Link
                  to="/profile/transfer-device"
                  class={cn(buttonVariants({ variant: "default" }), "gap-2")}
                >
                  <QrCode class="h-4 w-4" />
                  {t(msg`Pair device`)}
                </Link>
              </div>
            </div>

            <div class="p-5">
              {/* This device — the one certain member of the cloud */}
              <div class="flex items-center gap-3 rounded-xl border border-border/60 bg-surface-secondary/50 p-4">
                <span class="flex h-10 w-10 items-center justify-center rounded-lg bg-success/15 text-success">
                  <MonitorSmartphone class="h-5 w-5" />
                </span>
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-semibold text-foreground">
                    {t(msg`This device`)}
                  </p>
                  <p class="truncate text-xs text-muted-foreground">
                    {t(msg`Online · syncing locally`)}
                  </p>
                </div>
                <span class="flex items-center gap-1.5 text-xs font-medium text-success">
                  <span class="h-2 w-2 rounded-full bg-success" />
                  {t(msg`Active`)}
                </span>
              </div>

              {/* Empty state for other devices */}
              <div class="mt-4 flex flex-col items-center justify-center rounded-xl border border-dashed border-border/70 px-6 py-8 text-center">
                <span class="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 text-accent">
                  <Boxes class="h-6 w-6" />
                </span>
                <p class="text-sm font-semibold text-foreground">
                  {t(msg`No other devices paired yet`)}
                </p>
                <p class="mt-1 max-w-sm text-xs text-muted-foreground">
                  {t(
                    msg`Scan a one-time QR on another device to add it to your private cloud. Revoke access anytime — it's enforced on every request.`,
                  )}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: DashboardPage,
});
